import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  addField,
  getAllCompanies,
  selectAllCompanies,
} from "../redux/company/companySlice";

const AdminPanel = () => {
  const dispatch = useDispatch();
  const allCompanies = useSelector(selectAllCompanies);

  const [numFields, setNumFields] = useState(0);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [formName, setFormName] = useState("");

  useEffect(() => {
    dispatch(getAllCompanies());
  }, [dispatch]);

  const handleNumFieldsChange = (e) => {
    const count = parseInt(e.target.value, 10);
    if (!isNaN(count) && count >= 0) {
      setNumFields(count);
    }
  };

  const handleAdminChange = (e) => {
    setSelectedCompanyId(e.target.value);
  };

  const handleFormNameChange = (e) => {
    setFormName(e.target.value);
  };

  const formik = useFormik({
    initialValues: {
      fields: Array(numFields).fill({
        type: "text",
        label: "",
        name: "",
        placeholder: "",
        options: [],
      }),
    },
    onSubmit: (values, { resetForm }) => {
      if (!selectedCompanyId) {
        toast.error("Please select a company.");
        return;
      }
      if (!formName.trim()) {
        toast.error("Form name is required.");
        return;
      }
      const company = allCompanies.find(
        (company) => selectedCompanyId === company.id
      );
      if (company?.form?.name) {
        toast.error("Form is already created");
        return;
      }
      const formData = {
        name: formName,
        fields: values.fields,
        companyId: selectedCompanyId,
      };
      dispatch(addField(formData)).then((data) => {  
        if (data.payload.id) {
          toast.success("Form created successfully!");
          resetForm();
          setNumFields(0);
          setFormName("");
        } else {
          toast.error(`Error: ${data.payload.msg}`);
        }
      });
    },
    enableReinitialize: true,
  });

  const handleFieldChange = (index, e) => {
    const updatedFields = [...formik.values.fields];
    updatedFields[index] = {
      ...updatedFields[index],
      [e.target.name]: e.target.value,
    };
    formik.setFieldValue("fields", updatedFields);
  };

  const handleDropdownOptions = (index, optionIndex, value) => {
    const updatedFields = [...formik.values.fields];
    const updatedOptions = [...updatedFields[index].options];
    updatedOptions[optionIndex] = value;
    updatedFields[index] = { ...updatedFields[index], options: updatedOptions };
    formik.setFieldValue("fields", updatedFields);
  };

  const addDropdownOptions = (index, optionCount) => {
    const updatedFields = [...formik.values.fields];
    updatedFields[index].options = Array(optionCount).fill("");
    formik.setFieldValue("fields", updatedFields);
  };

  return (
    <div className="mt-16 mb-5 mx-auto max-w-2xl px-6 py-5 bg-white shadow-lg rounded-lg">
      <ToastContainer />
      <h1 className="text-3xl font-bold text-center mb-4">Create Form</h1>

      <div className="flex flex-col space-y-4">
        <div>
          <label className="block text-lg font-semibold mb-2">
            Select Company:
          </label>
          <select
            className="border border-gray-300 rounded-md mx-2 p-2 w-full"
            value={selectedCompanyId}
            onChange={handleAdminChange}
            required
          >
            <option value="" disabled>
              Select Company
            </option>
            {allCompanies?.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-lg font-semibold mb-2">
            Enter Form Name:
          </label>
          <input
            className="border border-gray-300 rounded-md mx-2 p-2 w-full"
            type="text"
            value={formName}
            onChange={handleFormNameChange}
            required
            placeholder="Enter the form name"
          />
        </div>

        <div>
          <label className="block text-lg font-semibold mb-2">
            Enter how many fields you require in the form:
          </label>
          <input
            className="border border-gray-300 rounded-md mx-2 p-2 w-full"
            type="number"
            value={numFields}
            onChange={handleNumFieldsChange}
            min="1"
            max="2"
            required
          />
        </div>
      </div>

      {numFields > 0 && (
        <form onSubmit={formik.handleSubmit} className="mt-6">
          {formik.values.fields.map((field, index) => (
            <div
              key={index}
              className="bg-gray-100 flex flex-col my-4 border border-gray-300 rounded-md p-4"
            >
              <div className="my-2">
                <label className="block font-medium mb-1">
                  Select the type of field
                </label>
                <select
                  className="border border-gray-300 rounded-md p-2 w-full"
                  name="type"
                  value={field.type}
                  onChange={(e) => handleFieldChange(index, e)}
                  required
                >
                  <option value="text">Text</option>
                  <option value="email">Email</option>
                  <option value="number">Number</option>
                  <option value="checkbox">Checkbox</option>
                  <option value="dropdown">Dropdown</option>
                </select>
              </div>

              {field.type === "dropdown" && (
                <div className="my-2">
                  <label className="block font-medium mb-1">
                    How many dropdown options do you want?
                  </label>
                  <input
                    type="number"
                    min="1"
                    className="border border-gray-300 rounded-md p-2 w-full"
                    onChange={(e) =>
                      addDropdownOptions(index, parseInt(e.target.value, 10))
                    }
                  />

                  {formik.values.fields[index].options.map(
                    (option, optionIndex) => (
                      <div key={optionIndex} className="my-2">
                        <label className="block font-medium mb-1">
                          Option {optionIndex + 1}
                        </label>
                        <input
                          type="text"
                          className="border border-gray-300 rounded-md p-2 w-full"
                          value={option}
                          onChange={(e) =>
                            handleDropdownOptions(
                              index,
                              optionIndex,
                              e.target.value
                            )
                          }
                          placeholder={`Enter option ${optionIndex + 1}`}
                        />
                      </div>
                    )
                  )}
                </div>
              )}

              <div className="my-2">
                <label className="block font-medium mb-1">Label</label>
                <input
                  className="border border-gray-300 rounded-md p-2 w-full"
                  name="label"
                  type="text"
                  placeholder="Enter the label"
                  value={field.label}
                  onChange={(e) => handleFieldChange(index, e)}
                  required
                />
              </div>

              <div className="my-2">
                <label className="block font-medium mb-1">Name</label>
                <input
                  className="border border-gray-300 rounded-md p-2 w-full"
                  name="name"
                  type="text"
                  placeholder="Enter the field name"
                  value={field.name}
                  onChange={(e) => handleFieldChange(index, e)}
                  required
                />
              </div>

              <div className="my-2">
                <label className="block font-medium mb-1">Placeholder</label>
                <input
                  className="border border-gray-300 rounded-md p-2 w-full"
                  name="placeholder"
                  type="text"
                  placeholder="Enter the placeholder"
                  value={field.placeholder}
                  onChange={(e) => handleFieldChange(index, e)}
                  required
                />
              </div>
            </div>
          ))}

          <div className="w-full text-right mt-4">
            <button
              className="bg-black py-2 px-6 rounded-md text-white"
              type="submit"
            >
              Submit
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default AdminPanel;
